import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MemoryGame, type GameStats, type PexesoPair } from './MemoryGame';
import {
  initLeaderboard,
  loadLeaderboardCache,
  loadUser,
  rankPlayers,
  saveUser,
  submitAttempt,
  type PexesoPlayer,
  type PexesoUser,
} from '../pexesoStore';
import './Hero.css';

const SKIP_FUNNY_COUNT = 4;

/* Pool of all Nicole+Jan pexeso pairs in Nicole's magenta-sketch style.
   Each page load we draw PAIRS_PER_GAME at random, so returning guests
   get a different mix every time. */
const PEXESO_POOL: PexesoPair[] = [
  { id: 'alt-01', photo: '/assets/photos/photo-alt-01.webp', cari: '/assets/photos/photo-alt-01-cari.webp', alt: 'on a boat' },
  // alt-02 (matcha+ring) disabled: only the legacy black/white sketch exists,
  // which clashes with the magenta pen style of the rest of the alt series.
  // Re-enable once a magenta cari is available.
  // { id: 'alt-02', photo: '/assets/photos/photo-alt-02.webp', cari: '/assets/photos/photo-01-cari.webp', alt: 'matcha selfie' },
  { id: 'alt-03', photo: '/assets/photos/photo-alt-03.webp', cari: '/assets/photos/photo-alt-03-cari.webp', alt: 'burger king crowns' },
  { id: 'alt-04', photo: '/assets/photos/photo-alt-04.webp', cari: '/assets/photos/photo-alt-04-cari.webp', alt: 'a kiss' },
  { id: 'alt-05', photo: '/assets/photos/photo-alt-05.webp', cari: '/assets/photos/photo-alt-05-cari.webp', alt: 'memory 5' },
  { id: 'alt-06', photo: '/assets/photos/photo-alt-06.webp', cari: '/assets/photos/photo-alt-06-cari.webp', alt: 'memory 6' },
  { id: 'alt-07', photo: '/assets/photos/photo-alt-07.webp', cari: '/assets/photos/photo-alt-07-cari.webp', alt: 'memory 7' },
  { id: 'alt-08', photo: '/assets/photos/photo-alt-08.webp', cari: '/assets/photos/photo-alt-08-cari.webp', alt: 'memory 8' },
  { id: 'new-01', photo: '/assets/photos/photo-new-01.webp', cari: '/assets/photos/photo-new-01-cari.webp', alt: 'green soldier' },
  { id: 'new-02', photo: '/assets/photos/photo-new-02.webp', cari: '/assets/photos/photo-new-02-cari.webp', alt: 'arc de triomphe' },
  { id: 'new-03', photo: '/assets/photos/photo-new-03.webp', cari: '/assets/photos/photo-new-03-cari.webp', alt: 'memory' },
  { id: 'new-04', photo: '/assets/photos/photo-new-04.webp', cari: '/assets/photos/photo-new-04-cari.webp', alt: 'memory' },
  { id: 'new-05', photo: '/assets/photos/photo-new-05.webp', cari: '/assets/photos/photo-new-05-cari.webp', alt: 'memory' },
  { id: 'new-06', photo: '/assets/photos/photo-new-06.webp', cari: '/assets/photos/photo-new-06-cari.webp', alt: 'memory' },
  { id: 'new-07', photo: '/assets/photos/photo-new-07.webp', cari: '/assets/photos/photo-new-07-cari.webp', alt: 'memory' },
  { id: 'new-08', photo: '/assets/photos/photo-new-08.webp', cari: '/assets/photos/photo-new-08-cari.webp', alt: 'memory' },
];

const PAIRS_PER_GAME = 8;
const TOTAL_REVEALS = PAIRS_PER_GAME;

function pickPairs(pool: PexesoPair[], n: number): PexesoPair[] {
  const copy = [...pool];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}

const POOL_URLS: string[] = PEXESO_POOL.flatMap((p) => [p.photo, p.cari]);
const POOL_PRELOAD_MAX_MS = 2500;

/* Flight animation — a floating overlay that launches from the midpoint
   of the two matched cards and glides to the position of the next reveal
   target. When it lands, the target gets `.is-revealed` and the overlay
   clears. On mobile (stacked layout) we also scroll the target into view
   before the flight and scroll back to the game after. */
type FlightStyle = 'item' | 'connector' | 'script' | 'invite';

type FlightState = {
  text: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  flightStyle: FlightStyle;
};

/** Map a reveal slot → the typography class the flight overlay should wear.
    Kept in sync with the reveal list order in the JSX below. */
function flightStyleFor(n: number): FlightStyle {
  if (n === 7) return 'script';
  if (n === 8) return 'invite';
  if (n === 2 || n === 4) return 'connector';
  return 'item';
}

const FLIGHT_DURATION_MS = 900;
const MOBILE_SCROLL_DELAY_MS = 550;   // wait after scrollIntoView before launching flight
const MOBILE_RETURN_DELAY_MS = 1400;  // after reveal, wait before scrolling back

type Props = {
  /** Fired when the player completes or skips the pexeso game. App uses
      this to unlock the RSVP section + nav link. Also fired on mount for
      returning guests so the page renders unlocked from the start. */
  onGameComplete?: () => void;
};

export function Hero({ onGameComplete }: Props) {
  const { t } = useTranslation();

  /* Returning guest (name cookie already set) skips the auto-play intro:
     reveals land fully visible and the game only mounts if they press the
     optional "play again" button. New guest (no cookie) sees the name
     prompt first; the game mounts once their name is saved. */
  const initialUser = useRef<PexesoUser | null | undefined>(undefined);
  if (initialUser.current === undefined) initialUser.current = loadUser();
  const hasReturningUser = !!initialUser.current;

  const [pairsFound, setPairsFound] = useState(
    hasReturningUser ? TOTAL_REVEALS : 0,
  );
  const [gameComplete, setGameComplete] = useState(hasReturningUser);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameKey, setGameKey] = useState(0);
  const [flight, setFlight] = useState<FlightState | null>(null);
  const [flightMoving, setFlightMoving] = useState(false);
  /* Flips the tagline from "it starts with a swipe" to "it started..."
     once the player has performed the intro swipe gesture. Persists across
     Play Again since the invitation to swipe is a one-time intro cue. */
  const [hasSwiped, setHasSwiped] = useState(hasReturningUser);

  /* Block the game mount until every pool image is fully loaded (or we hit
     the safety timeout). Cold caches were otherwise letting the peek phase
     start with one or two images still decoding, which the eye catches as
     a flicker on exactly one card. */
  const [poolReady, setPoolReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    /* Use Image.decode() — it resolves only once the bytes are fully
       decoded into a ready-to-paint bitmap, which is what actually
       prevents the "one card settles a frame later" flicker. Plain
       load events fire earlier (bytes arrived ≠ decode done). */
    const ready = POOL_URLS.map((url) => {
      const img = new Image();
      img.src = url;
      return img.decode().catch(() => undefined);
    });

    Promise.all(ready).then(() => {
      if (!cancelled) setPoolReady(true);
    });

    const safety = window.setTimeout(() => {
      if (!cancelled) setPoolReady(true);
    }, POOL_PRELOAD_MAX_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(safety);
    };
  }, []);

  /* Guest identity + SHARED server-backed leaderboard. Name prompt renders
     inline when no name is stored; future game results are POSTed and the
     full board refetched, so every browser sees the same ranking. The
     initial render uses the localStorage cache for instant paint, then a
     mount effect fetches the authoritative board (and migrates any
     pre-backend local-only history on the first run). */
  const [user, setUser] = useState<PexesoUser | null>(initialUser.current);
  const [nameDraft, setNameDraft] = useState('');
  const [leaderboard, setLeaderboard] = useState<PexesoPlayer[]>(() => loadLeaderboardCache());
  const [showMigrationNotice, setShowMigrationNotice] = useState(false);
  const [lastSkipped, setLastSkipped] = useState(false);
  const [skipFunnyIdx, setSkipFunnyIdx] = useState<number>(() =>
    Math.floor(Math.random() * SKIP_FUNNY_COUNT),
  );

  /* Freeze the drawn pairs per game instance. Using a ref (instead of
     useMemo) makes pickPairs run exactly once per gameKey — React can
     legally discard a useMemo's cached value, which caused a flicker
     where the same <img> briefly showed a different pool photo. */
  const pairsCache = useRef<Record<number, PexesoPair[]>>({});
  if (!pairsCache.current[gameKey]) {
    pairsCache.current[gameKey] = pickPairs(PEXESO_POOL, PAIRS_PER_GAME);
  }
  const pairs = pairsCache.current[gameKey];

  const targetRefs = useRef<Map<number, HTMLElement>>(new Map());
  const gameRef = useRef<HTMLDivElement>(null);

  const setTargetRef = (n: number) => (el: HTMLElement | null) => {
    if (el) targetRefs.current.set(n, el);
    else targetRefs.current.delete(n);
  };

  const revealed = (n: number) => pairsFound >= n;

  useEffect(() => {
    if (gameComplete) document.body.classList.add('game-complete');
  }, [gameComplete]);

  /* Fetch the shared leaderboard on mount, running the one-shot migration
     of pre-backend localStorage data if any exists. Cache-rendered board
     is replaced as soon as the server responds. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { board, migrated } = await initLeaderboard();
      if (cancelled) return;
      setLeaderboard(board);
      if (migrated) setShowMigrationNotice(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const startGame = () => {
    setGameStarted(true);
    setPairsFound(0);
    setGameComplete(false);
    setLastSkipped(false);
    setSkipFunnyIdx(Math.floor(Math.random() * SKIP_FUNNY_COUNT));
    setGameKey((k) => k + 1);
    setFlight(null);
    setFlightMoving(false);
  };

  const recordResult = (stats: GameStats, skipped: boolean) => {
    if (!user) return;
    setLastSkipped(skipped);
    /* Fire-and-forget: UI doesn't block on the network round-trip. The
       cache (rendered now) updates when the POST + refetch resolves. */
    submitAttempt(user.name, stats.clicks, skipped).then((board) => {
      setLeaderboard(board);
    });
  };

  const handleSkip = (stats: GameStats) => {
    setFlight(null);
    setFlightMoving(false);
    setPairsFound(TOTAL_REVEALS);
    setGameComplete(true);
    recordResult(stats, true);
    onGameComplete?.();
  };

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = nameDraft.trim();
    if (!trimmed) return;
    setUser(saveUser(trimmed));
    setNameDraft('');
    /* Kick the game off now that we have a name to record against. */
    startGame();
  };

  const handlePairFound = useCallback((rects: DOMRect[]) => {
    const nextN = Math.min(pairsFound + 1, TOTAL_REVEALS);
    const targetEl = targetRefs.current.get(nextN);

    if (!targetEl || rects.length !== 2) {
      setPairsFound((p) => Math.min(p + 1, TOTAL_REVEALS));
      return;
    }

    const isMobile = window.matchMedia('(max-width: 960px)').matches;

    const runFlight = () => {
      const tr = targetEl.getBoundingClientRect();
      const fromX = (rects[0].left + rects[0].right + rects[1].left + rects[1].right) / 4;
      const fromY = (rects[0].top + rects[0].bottom + rects[1].top + rects[1].bottom) / 4;
      const toX = tr.left;
      const toY = tr.top;

      /* Tell the target's sparkle sweep to originate from the flight's
         direction (CSS vars read by .hero__*.is-revealed::before). The
         leftover sparkles then read as a continuation of the letters'
         trajectory instead of a generic left→right wash. */
      {
        const dx = fromX - (toX + tr.width / 2);
        const dy = fromY - (toY + tr.height / 2);
        const len = Math.hypot(dx, dy) || 1;
        const MAG = 70; // percent of ::before element
        const originX = ((dx / len) * MAG).toFixed(1);
        const originY = ((dy / len) * MAG).toFixed(1);
        targetEl.style.setProperty('--sparkle-from-x', `${originX}%`);
        targetEl.style.setProperty('--sparkle-from-y', `${originY}%`);
        targetEl.style.setProperty('--sparkle-to-x', `${-originX}%`);
        targetEl.style.setProperty('--sparkle-to-y', `${-originY}%`);
      }

      setFlight({
        text: (targetEl.textContent || '').trim(),
        from: { x: fromX, y: fromY },
        to: { x: toX, y: toY },
        flightStyle: flightStyleFor(nextN),
      });

      // Two RAFs so the browser commits the "from" position before we
      // apply the transform to "to" — that's what makes CSS animate it.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setFlightMoving(true));
      });

      // When the flight lands: reveal the real target, clear the overlay,
      // and on mobile glide the game back into view.
      window.setTimeout(() => {
        setPairsFound((p) => Math.min(p + 1, TOTAL_REVEALS));
        setFlight(null);
        setFlightMoving(false);
        if (isMobile) {
          window.setTimeout(() => {
            gameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, MOBILE_RETURN_DELAY_MS);
        }
      }, FLIGHT_DURATION_MS + 80);
    };

    if (isMobile) {
      targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(runFlight, MOBILE_SCROLL_DELAY_MS);
    } else {
      runFlight();
    }
  }, [pairsFound]);

  // When reveal becomes final (gameComplete), also fire onComplete handling.
  const handleComplete = (stats: GameStats) => {
    setGameComplete(true);
    recordResult(stats, false);
    onGameComplete?.();
  };

  return (
    <section className="hero">
      <div className="hero__stage">
        <div className="hero__game" ref={gameRef}>
          <div className="hero__tagline">
            {/* 3D Y-axis flip between present/past tense, triggered by the
                intro swipe gesture from MemoryGame. */}
            <div className={`hero__tagline-flip ${hasSwiped ? 'is-flipped' : ''}`}>
              <span className="hero__tagline-face hero__tagline-face--front">
                {t('hero.taglinePresent')}
              </span>
              <span className="hero__tagline-face hero__tagline-face--back">
                {t('hero.taglinePast')}
              </span>
            </div>
          </div>
          <h2 className="hero__game-heading">{t('hero.gameHeading')}</h2>

          {user ? (
            <div className="hero__greeting" aria-live="polite">
              {t('hero.greeting', { name: user.name })}
            </div>
          ) : (
            <form className="hero__name-prompt" onSubmit={handleNameSubmit}>
              <label htmlFor="hero-name-input" className="hero__name-label">
                {t('hero.namePromptLabel')}
              </label>
              <div className="hero__name-row">
                <input
                  id="hero-name-input"
                  type="text"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  placeholder={t('hero.namePromptPlaceholder')}
                  maxLength={32}
                  autoComplete="given-name"
                  className="hero__name-input"
                />
                <button type="submit" className="hero__name-submit" disabled={!nameDraft.trim()}>
                  {t('hero.namePromptSubmit')}
                </button>
              </div>
            </form>
          )}

          {gameStarted && poolReady && (
            <MemoryGame
              key={gameKey}
              pairs={pairs}
              onPairFound={handlePairFound}
              onComplete={handleComplete}
              onSkip={handleSkip}
              onSwiped={() => setHasSwiped(true)}
            />
          )}
          {gameStarted && !poolReady && (
            <div className="memory-game memory-game--loading" aria-hidden />
          )}
          {!gameStarted && user && (
            <button
              type="button"
              className="hero__start-game"
              onClick={startGame}
            >
              {t('hero.startGame')} ↻
            </button>
          )}
        </div>

        <div className="hero__type">
          <div className="hero__reveal-list">
            <div
              ref={setTargetRef(1)}
              className={`hero__reveal-item ${revealed(1) ? 'is-revealed' : ''}`}
            >
              {t('hero.saveTheDate')},
            </div>
            <div
              ref={setTargetRef(2)}
              className={`hero__reveal-item hero__reveal-item--connector ${revealed(2) ? 'is-revealed' : ''}`}
            >
              {t('hero.forConnector')},
            </div>
            <div
              ref={setTargetRef(3)}
              className={`hero__reveal-item ${revealed(3) ? 'is-revealed' : ''}`}
            >
              {t('hero.datesCompact')},
            </div>
            <div
              ref={setTargetRef(4)}
              className={`hero__reveal-item hero__reveal-item--connector ${revealed(4) ? 'is-revealed' : ''}`}
            >
              {t('hero.inConnector')},
            </div>
            <div
              ref={setTargetRef(5)}
              className={`hero__reveal-item ${revealed(5) ? 'is-revealed' : ''}`}
            >
              {t('hero.hotelShort')},
            </div>
            <div
              ref={setTargetRef(6)}
              className={`hero__reveal-item ${revealed(6) ? 'is-revealed' : ''}`}
            >
              {t('hero.weddingWeekend')},
            </div>
          </div>

          <h1
            ref={setTargetRef(7)}
            className={`hero__script ${revealed(7) ? 'is-revealed' : ''}`}
            aria-label={t('hero.scriptLine')}
          >
            {t('hero.scriptLine')}
          </h1>

          <div
            ref={setTargetRef(8)}
            className={`hero__invite ${revealed(8) ? 'is-revealed' : ''}`}
          >
            {t('hero.inviteLine')}
          </div>

          <div className={`hero__rsvp ${revealed(8) ? 'is-revealed' : ''}`}>
            {gameComplete && gameStarted && (
              <button
                type="button"
                className="hero__play-again"
                onClick={startGame}
              >
                {t('hero.playAgainNewPhotos')} ↻
              </button>
            )}
            {gameComplete && leaderboard.length > 0 && (
              <div className="hero__leaderboard">
                <h3 className="hero__leaderboard-title">
                  {t('hero.leaderboardTitle')}
                </h3>
                {showMigrationNotice && (
                  <p
                    className="hero__leaderboard-migration"
                    role="status"
                    onClick={() => setShowMigrationNotice(false)}
                  >
                    {t('hero.leaderboardMigrationNotice')}
                  </p>
                )}
                {lastSkipped && (
                  <p className="hero__skip-funny">
                    {t(`hero.skipFunny${skipFunnyIdx}`)}
                  </p>
                )}
                <ol className="hero__leaderboard-list">
                  {rankPlayers(leaderboard)
                    .slice(0, 5)
                    .map((r, idx) => {
                      const isMe = !!user && r.name === user.name;
                      const onlySkips = r.bestClicks == null;
                      return (
                        <li
                          key={r.name}
                          className={[
                            'hero__leaderboard-row',
                            onlySkips ? 'is-skipped' : '',
                            isMe ? 'is-me' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <span className="hero__leaderboard-rank">
                            {onlySkips ? '💨' : `${idx + 1}.`}
                          </span>
                          <span className="hero__leaderboard-name">{r.name}</span>
                          <span className="hero__leaderboard-score">
                            {onlySkips
                              ? t('hero.leaderboardSkipsOnly', { count: r.skipCount })
                              : t('hero.leaderboardSummary', {
                                  attempts: r.attempts,
                                  best: r.bestClicks,
                                  first: r.firstClicks ?? r.bestClicks,
                                })}
                          </span>
                        </li>
                      );
                    })}
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Flight overlay — renders the next reveal text at the matched pair's
          midpoint, then glides to the target's position. Fades out on land,
          at which moment the real target gets `.is-revealed`. */}
      {flight && (
        <div
          aria-hidden
          className={`hero__flight hero__flight--${flight.flightStyle} ${flightMoving ? 'is-landing' : ''}`}
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            transform: flightMoving
              ? `translate(${flight.to.x}px, ${flight.to.y}px) scale(0.95)`
              : `translate(${flight.from.x - 60}px, ${flight.from.y - 14}px) scale(1.25)`,
            transition: `transform ${FLIGHT_DURATION_MS}ms cubic-bezier(0.45, 0, 0.25, 1), opacity 260ms ease-out ${FLIGHT_DURATION_MS - 220}ms`,
            opacity: flightMoving ? 0 : 1,
            // Dynamic drift direction for the trailing sparkle so it continues
            // past the landing point in the same direction the letters flew.
            // Cast to Record to allow custom CSS properties in the style prop.
            ...({
              '--flight-drift-x': `${Math.sign(flight.to.x - flight.from.x) * 70}px`,
              '--flight-drift-y': `${Math.sign(flight.to.y - flight.from.y) * 40}px`,
            } as Record<string, string>),
          }}
        >
          <span className="hero__flight__sparkle" aria-hidden />
          <span className="hero__flight__text">{flight.text}</span>
        </div>
      )}
    </section>
  );
}
